/**
 * Pending OP month/week UI: week row slots use Monday-based weeks (`weekStartsOn: 1`).
 * If generated timesheets use a different anchor, adjust `getMondayWeekStartsInMonth` accordingly.
 */
import { eachWeekOfInterval, endOfMonth, format, startOfMonth } from 'date-fns';
import { Timesheet } from '../types';
import { getWeeklyTimesheetWeekStart } from './getWeeklyTimesheetWeekStart';

export function normalizeWeekStartKey(weekStart?: string): string {
  if (!weekStart) return '';
  return weekStart.split('T')[0];
}

/** Keys that may identify the same timesheet row for week-slot matching. */
export function timesheetWeekKeys(ts: Timesheet): string[] {
  const keys = new Set<string>();
  const canonical = normalizeWeekStartKey(getWeeklyTimesheetWeekStart(ts));
  if (canonical) keys.add(canonical);
  const ws = normalizeWeekStartKey(ts.weekStart);
  if (ws) keys.add(ws);
  if (ts.weekDates[0]) keys.add(normalizeWeekStartKey(ts.weekDates[0]));
  return [...keys];
}

export function weekStartFallsInCalendarMonth(
  weekStartKey: string,
  year: number,
  month: number
): boolean {
  if (!weekStartKey || !/^\d{4}-\d{2}-\d{2}$/.test(weekStartKey)) return false;
  const [y, m] = weekStartKey.split('-').map(Number);
  return y === year && m === month;
}

export function getMondayWeekStartsInMonth(year: number, month: number): string[] {
  const monthStart = startOfMonth(new Date(year, month - 1, 1));
  const monthEnd = endOfMonth(monthStart);
  return eachWeekOfInterval({ start: monthStart, end: monthEnd }, { weekStartsOn: 1 }).map((d) =>
    format(d, 'yyyy-MM-dd')
  );
}

/**
 * Week row keys for a calendar month: Monday-based grid weeks intersecting the month,
 * plus any pending timesheet week starts in that month not already listed.
 */
export function getMergedWeekSlotKeysForMonth(
  year: number,
  month: number,
  pendingInMonth: Timesheet[]
): string[] {
  const slotSet = new Set<string>(getMondayWeekStartsInMonth(year, month));
  for (const ts of pendingInMonth) {
    for (const k of timesheetWeekKeys(ts)) {
      if (weekStartFallsInCalendarMonth(k, year, month)) slotSet.add(k);
    }
  }
  return [...slotSet].sort((a, b) => a.localeCompare(b));
}

export function findTimesheetForWeekSlot(
  slotKey: string,
  pendingTimesheets: Timesheet[]
): Timesheet | undefined {
  return pendingTimesheets.find((ts) => timesheetWeekKeys(ts).includes(slotKey));
}

/** Parse `yyyy-M-d` or `yyyy-MM-dd` (and optional `T…` suffix) to UTC midnight ms. */
function utcMsFromYmdKey(key: string): number | null {
  const datePart = key.split('T')[0];
  const segs = datePart.split('-');
  if (segs.length < 3) return null;
  const y = Number(segs[0]);
  const m = Number(segs[1]);
  const d = Number(segs[2]);
  if (![y, m, d].every((n) => Number.isFinite(n))) return null;
  const t = Date.UTC(y, m - 1, d);
  return Number.isNaN(t) ? null : t;
}

/**
 * Chronological order by week start. Uses numeric calendar comparison so API values
 * like `2026-2-5` (unpadded) sort correctly; `localeCompare` alone would not.
 */
export function compareTimesheetsByWeekStart(a: Timesheet, b: Timesheet): number {
  const ka = normalizeWeekStartKey(getWeeklyTimesheetWeekStart(a));
  const kb = normalizeWeekStartKey(getWeeklyTimesheetWeekStart(b));
  const ta = utcMsFromYmdKey(ka);
  const tb = utcMsFromYmdKey(kb);
  if (ta !== null && tb !== null && ta !== tb) return ta - tb;
  if (ta === null && tb !== null) return 1;
  if (ta !== null && tb === null) return -1;
  if (ta !== null && tb !== null) return a.id - b.id;
  const byStr = ka.localeCompare(kb);
  if (byStr !== 0) return byStr;
  return a.id - b.id;
}

export function filterTimesheetsByDealSearch(timesheets: Timesheet[], search: string): Timesheet[] {
  const q = search.trim().toLowerCase();
  if (!q) return timesheets;
  return timesheets.filter((ts) =>
    ts.timesheetRows.some((row) => row.name.toLowerCase().includes(q))
  );
}

/** DOM id for scrolling to one stacked week on the pending month page. */
export function pendingMonthTimesheetDomId(timesheetId: number | string): string {
  return `pending-month-ts-${timesheetId}`;
}

/** Calendar year/month of the timesheet’s week start (same bucketing as the month stacked view). */
export function getCalendarYearMonthFromTimesheetWeekStart(
  ts: Timesheet
): { year: number; month: number } | null {
  const key = normalizeWeekStartKey(getWeeklyTimesheetWeekStart(ts));
  if (!key) return null;
  const parts = key.split('-');
  if (parts.length < 2) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}
